import { PendingWrite } from "@langchain/langgraph-checkpoint";
import {
  coerceTimeoutPolicy,
  type TimeoutPolicy,
} from "./pregel/utils/timeout.js";

/** Special reserved node name denoting the start of a graph. */
export const START = "__start__";
/** Special reserved node name denoting the end of a graph. */
export const END = "__end__";
export const INPUT = "__input__";
export const COPY = "__copy__";
export const ERROR = "__error__";
/**
 * Special reserved write key recording the name of the node whose execution
 * failed, so node-level error handlers see the same failure provenance after a
 * checkpoint resume. Value format in pending writes:
 * `[taskId, ERROR_SOURCE_NODE, nodeName: string]`.
 */
export const ERROR_SOURCE_NODE = "__error_source_node__";

/** Special reserved cache namespaces */
export const CACHE_NS_WRITES = "__pregel_ns_writes";

/**
 * System-wide upper bound on how many supersteps a {@link DeltaChannel} may go
 * without writing a {@link DeltaSnapshot} blob. Once a channel's
 * supersteps-since-snapshot counter reaches this value, a snapshot is forced
 * even if the channel's own `snapshotFrequency` has not been reached — this
 * prevents unbounded ancestor walks on threads where a delta channel exists
 * but is no longer being updated.
 *
 * Overridable via the `LANGGRAPH_DELTA_MAX_SUPERSTEPS_SINCE_SNAPSHOT`
 * environment variable. Read lazily so test/runtime overrides take effect.
 *
 * @remarks Beta.
 */
export function getDeltaMaxSuperstepsSinceSnapshot(): number {
  const raw =
    typeof process !== "undefined"
      ? process.env?.LANGGRAPH_DELTA_MAX_SUPERSTEPS_SINCE_SNAPSHOT
      : undefined;
  if (raw !== undefined && raw !== "") {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 5000;
}

export const CONFIG_KEY_SEND = "__pregel_send";
/** config key containing function used to call a node (push task) */
export const CONFIG_KEY_CALL = "__pregel_call";
export const CONFIG_KEY_READ = "__pregel_read";
export const CONFIG_KEY_CHECKPOINTER = "__pregel_checkpointer";
export const CONFIG_KEY_RESUMING = "__pregel_resuming";
export const CONFIG_KEY_TASK_ID = "__pregel_task_id";
export const CONFIG_KEY_STREAM = "__pregel_stream";
export const CONFIG_KEY_RESUME_VALUE = "__pregel_resume_value";
export const CONFIG_KEY_RESUME_MAP = "__pregel_resume_map";
export const CONFIG_KEY_SCRATCHPAD = "__pregel_scratchpad";
/** config key containing state from previous invocation of graph for the given thread */
export const CONFIG_KEY_PREVIOUS_STATE = "__pregel_previous";
export const CONFIG_KEY_DURABILITY = "__pregel_durability";
export const CONFIG_KEY_CHECKPOINT_ID = "checkpoint_id";
export const CONFIG_KEY_CHECKPOINT_NS = "checkpoint_ns";

export const CONFIG_KEY_NODE_FINISHED = "__pregel_node_finished";

/**
 * Config key holding a {@link NodeError} (failed source node + error) for the
 * current node-level error handler invocation. Injected when an error handler
 * task is prepared after the failing node's retry policy is exhausted.
 */
export const CONFIG_KEY_NODE_ERROR = "__pregel_node_error";

// this one is part of public API
export const CONFIG_KEY_CHECKPOINT_MAP = "checkpoint_map";

export const CONFIG_KEY_REPLAY_STATE = "__pregel_replay_state";

export const CONFIG_KEY_ABORT_SIGNALS = "__pregel_abort_signals";

/** Special channel reserved for graph interrupts */
export const INTERRUPT = "__interrupt__";
/** Special channel reserved for graph resume */
export const RESUME = "__resume__";
/** Special channel reserved for cases when a task exits without any writes */
export const NO_WRITES = "__no_writes__";
/** Special channel reserved for graph return */
export const RETURN = "__return__";
/** Special channel reserved for graph previous state */
export const PREVIOUS = "__previous__";
export const RUNTIME_PLACEHOLDER = "__pregel_runtime_placeholder__";
export const RECURSION_LIMIT_DEFAULT = 25;

export const TAG_HIDDEN = "langsmith:hidden";
export const TAG_NOSTREAM = "langsmith:nostream";
export const SELF = "__self__";

export const TASKS = "__pregel_tasks";
export const PUSH = "__pregel_push";
export const PULL = "__pregel_pull";

export const TASK_NAMESPACE = "6ba7b831-9dad-11d1-80b4-00c04fd430c8";
export const NULL_TASK_ID = "00000000-0000-0000-0000-000000000000";

export const RESERVED = [
  TAG_HIDDEN,
  INPUT,
  INTERRUPT,
  RESUME,
  ERROR,
  ERROR_SOURCE_NODE,
  NO_WRITES,

  // reserved config.configurable keys
  CONFIG_KEY_SEND,
  CONFIG_KEY_READ,
  CONFIG_KEY_CHECKPOINTER,
  CONFIG_KEY_DURABILITY,
  CONFIG_KEY_STREAM,
  CONFIG_KEY_RESUMING,
  CONFIG_KEY_TASK_ID,
  CONFIG_KEY_CALL,
  CONFIG_KEY_RESUME_VALUE,
  CONFIG_KEY_SCRATCHPAD,
  CONFIG_KEY_PREVIOUS_STATE,
  CONFIG_KEY_CHECKPOINT_MAP,
  CONFIG_KEY_CHECKPOINT_NS,
  CONFIG_KEY_CHECKPOINT_ID,
  CONFIG_KEY_REPLAY_STATE,
];

export const CHECKPOINT_NAMESPACE_SEPARATOR = "|";
export const CHECKPOINT_NAMESPACE_END = ":";

/**
 * Symbol used internally to identify Command instances.
 * Exported to support cross-version type compatibility.
 * @internal
 */
export const COMMAND_SYMBOL = Symbol.for("langgraph.command");

/**
 * Instance of a {@link Command} class.
 *
 * This is used to avoid IntelliSense suggesting public fields
 * of {@link Command} class when a plain object is expected.
 *
 * @see {@link Command}
 * @internal
 */
export class CommandInstance<
  Resume = unknown,
  Update = Record<string, unknown>,
  Nodes extends string = string,
> {
  [COMMAND_SYMBOL]: CommandParams<Resume, Update, Nodes>;

  constructor(args: CommandParams<Resume, Update, Nodes>) {
    this[COMMAND_SYMBOL] = args;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SendInterface<Node extends string = string, Args = any> {
  node: Node;
  args: Args;
  /**
   * Optional per-task timeout policy that overrides the target node's timeout
   * for this specific pushed task.
   */
  timeout?: TimeoutPolicy;
}

/** Keyword options for {@link Send}. */
export type SendOptions = {
  /**
   * Per-task timeout policy overriding the target node's timeout for this
   * pushed task. A bare number is treated as `runTimeout` (milliseconds).
   */
  timeout?: number | TimeoutPolicy;
};

export function _isSendInterface(x: unknown): x is SendInterface {
  const operation = x as SendInterface;
  return (
    operation !== null &&
    operation !== undefined &&
    typeof operation.node === "string" &&
    operation.args !== undefined
  );
}

/**
 *
 * A message or packet to send to a specific node in the graph.
 *
 * The `Send` class is used within a `StateGraph`'s conditional edges to
 * dynamically invoke a node with a custom state at the next step.
 *
 * Importantly, the sent state can differ from the core graph's state,
 * allowing for flexible and dynamic workflow management.
 *
 * One such example is a "map-reduce" workflow where your graph invokes
 * the same node multiple times in parallel with different states,
 * before aggregating the results back into the main graph's state.
 *
 * @example
 * ```typescript
 * import { Annotation, Send, StateGraph } from "@langchain/langgraph";
 *
 * const ChainState = Annotation.Root({
 *   subjects: Annotation<string[]>,
 *   jokes: Annotation<string[]>({
 *     reducer: (a, b) => a.concat(b),
 *   }),
 * });
 *
 * const continueToJokes = async (state: typeof ChainState.State) => {
 *   return state.subjects.map((subject) => {
 *     return new Send("generate_joke", { subjects: [subject] });
 *   });
 * };
 *
 * @remarks
 * A per-task timeout can be supplied via the third argument's `timeout` option
 * to override the target node's configured timeout for this specific pushed task:
 *
 * ```typescript
 * new Send("generate_joke", { subjects: [subject] }, { timeout: { idleTimeout: 5000 } });
 * ```
 *
 * const graph = new StateGraph(ChainState)
 *   .addNode("generate_joke", (state) => ({
 *     jokes: [`Joke about ${state.subjects}`],
 *   }))
 *   .addConditionalEdges("__start__", continueToJokes)
 *   .addEdge("generate_joke", "__end__")
 *   .compile();
 *
 * const res = await graph.invoke({ subjects: ["cats", "dogs"] });
 * console.log(res);
 *
 * // Invoking with two subjects results in a generated joke for each
 * // { subjects: ["cats", "dogs"], jokes: [`Joke about cats`, `Joke about dogs`] }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Send<
  Node extends string = string,
  Args = any,
> implements SendInterface<Node, Args> {
  lg_name = "Send";

  public node: Node;

  public args: Args;

  /**
   * Optional per-task timeout policy that overrides the target node's timeout
   * for this specific pushed task. A bare number is treated as a hard
   * `runTimeout` (in milliseconds).
   */
  public timeout?: TimeoutPolicy;

  constructor(node: Node, args: Args, options?: SendOptions) {
    this.node = node;
    this.args = _deserializeCommandSendObjectGraph(args) as Args;
    this.timeout = coerceTimeoutPolicy(options?.timeout);
  }

  toJSON() {
    return {
      lg_name: this.lg_name,
      node: this.node,
      args: this.args,
      timeout: this.timeout,
    };
  }
}

export function _isSend(x: unknown): x is Send {
  // eslint-disable-next-line no-instanceof/no-instanceof
  return x instanceof Send;
}

export const OVERWRITE = "__overwrite__";

/**
 * An object representing a direct overwrite of a value for a channel.
 * Used to signal that the channel value should be replaced with the given value,
 * bypassing any reducer or binary operator logic.
 *
 * @template ValueType - The type of the value being overwritten.
 * @property {ValueType} [OVERWRITE] - The value to directly set.
 *
 * @example
 * const overwriteObj: OverwriteValue<number> = { __overwrite__: 123 };
 */
export interface OverwriteValue<ValueType> {
  [OVERWRITE]: ValueType;
}

/**
 * Bypass a reducer and write the wrapped value directly to a
 * {@link BinaryOperatorAggregate} channel.
 *
 * Receiving multiple `Overwrite` values for the same channel in a single
 * super-step will raise an {@link InvalidUpdateError}.
 *
 * @example
 * ```typescript
 * import { Annotation, StateGraph, Overwrite } from "@langchain/langgraph";
 *
 * const State = Annotation.Root({
 *   messages: Annotation<string[]>({
 *     reducer: (a, b) => a.concat(b),
 *     default: () => [],
 *   }),
 * });
 *
 * const replaceMessages = (_state: typeof State.State) => {
 *   return { messages: new Overwrite(["replacement"]) };
 * };
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Overwrite<ValueType = any> implements OverwriteValue<ValueType> {
  lg_name = "Overwrite";

  readonly [OVERWRITE]: ValueType;

  constructor(value: ValueType) {
    this[OVERWRITE] = value;
  }

  get value(): ValueType {
    return this[OVERWRITE];
  }

  toJSON() {
    return { [OVERWRITE]: this[OVERWRITE] };
  }

  static isInstance<ValueType>(value: unknown): value is Overwrite<ValueType> {
    if (!value || typeof value !== "object") return false;
    if (OVERWRITE in value) return true;
    if ("lg_name" in value && value.lg_name === "Overwrite") return true;
    return false;
  }
}

/**
 * Helper function to detect and extract the value from an Overwrite wrapper,
 * supporting both the Overwrite class instance and the serialized object format.
 *
 * Use to check if a provided value represents an Overwrite: returns the
 * unwrapped value if so, or undefined otherwise.
 *
 * - If the value is an Overwrite instance (preferred API), return its `.value`.
 * - If the value is a wire-format object ({ [OVERWRITE]: value }), extract it.
 * - Otherwise, returns undefined.
 *
 * @template ValueType - The expected type of the Overwrite value.
 * @param value - The value to check (may be anything).
 * @returns The unwrapped value if value is an Overwrite, or undefined otherwise.
 * @internal
 */
export function _getOverwriteValue<ValueType>(
  value: unknown
): [true, ValueType] | [false, undefined] {
  if (typeof value === "object" && value !== null && OVERWRITE in value) {
    return [true, (value as Record<string, ValueType>)[OVERWRITE]];
  }
  return [false, undefined];
}

/**
 * Type guard to check if a value is an Overwrite value -- either the class
 * instance or the wire format object.
 *
 * @template ValueType - The expected type of the Overwrite value.
 * @param value - The value to check (may be anything).
 * @returns `true` if the value is an Overwrite value, `false` otherwise.
 * @internal
 */
export function _isOverwriteValue<ValueType>(
  value: unknown
): value is OverwriteValue<ValueType> {
  return _getOverwriteValue<ValueType>(value)[0];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Interrupt<Value = any> = {
  id?: string;
  value?: Value;
};

/**
 * Checks if the given graph invoke / stream chunk contains interrupt.
 *
 * @example
 * ```ts
 * import { INTERRUPT, isInterrupted } from "@langchain/langgraph";
 *
 * const values = await graph.invoke({ foo: "bar" });
 * if (isInterrupted<string>(values)) {
 *   const interrupt = values[INTERRUPT][0].value;
 * }
 * ```
 *
 * @param values - The values to check.
 * @returns `true` if the values contain an interrupt, `false` otherwise.
 */
export function isInterrupted<Value = unknown>(
  values: unknown
): values is { [INTERRUPT]: Interrupt<Value>[] } {
  if (!values || typeof values !== "object") return false;
  if (!(INTERRUPT in values)) return false;
  return Array.isArray(values[INTERRUPT]);
}

export type CommandParams<
  Resume = unknown,
  Update = Record<string, unknown>,
  Nodes extends string = string,
> = {
  /**
   * A discriminator field used to identify the type of object. Must be populated when serializing.
   *
   * Optional because it's not required to specify this when directly constructing a {@link Command}
   * object.
   */
  lg_name?: "Command";

  /**
   * Value to resume execution with. To be used together with {@link interrupt}.
   */
  resume?: Resume;
  /**
   * Graph to send the command to. Supported values are:
   *   - None: the current graph (default)
   *   - The specific name of the graph to send the command to
   *   - {@link Command.PARENT}: closest parent graph (only supported when returned from a node in a subgraph)
   */
  graph?: string;

  /**
   * Update to apply to the graph's state.
   */
  update?: Update | [string, unknown][];

  /**
   * Can be one of the following:
   *   - name of the node to navigate to next (any node that belongs to the specified `graph`)
   *   - sequence of node names to navigate to next
   *   - `Send` object (to execute a node with the input provided)
   *   - sequence of `Send` objects
   */
  goto?:
    | Nodes
    | SendInterface<Nodes> // eslint-disable-line @typescript-eslint/no-explicit-any
    | (Nodes | SendInterface<Nodes>)[]; // eslint-disable-line @typescript-eslint/no-explicit-any
};

/**
 * One or more commands to update the graph's state and send messages to nodes.
 * Can be used to combine routing logic with state updates in lieu of conditional edges
 *
 * @example
 * ```ts
 * import { Annotation, Command } from "@langchain/langgraph";
 *
 * // Define graph state
 * const StateAnnotation = Annotation.Root({
 *   foo: Annotation<string>,
 * });
 *
 * // Define the nodes
 * const nodeA = async (_state: typeof StateAnnotation.State) => {
 *   console.log("Called A");
 *   // this is a replacement for a real conditional edge function
 *   const goto = Math.random() > .5 ? "nodeB" : "nodeC";
 *   // note how Command allows you to BOTH update the graph state AND route to the next node
 *   return new Command({
 *     // this is the state update
 *     update: {
 *       foo: "a",
 *     },
 *     // this is a replacement for an edge
 *     goto,
 *   });
 * };
 *
 * // Nodes B and C are unchanged
 * const nodeB = async (state: typeof StateAnnotation.State) => {
 *   console.log("Called B");
 *   return {
 *     foo: state.foo + "|b",
 *   };
 * }
 *
 * const nodeC = async (state: typeof StateAnnotation.State) => {
 *   console.log("Called C");
 *   return {
 *     foo: state.foo + "|c",
 *   };
 * }
 * 
 * import { StateGraph } from "@langchain/langgraph";

 * // NOTE: there are no edges between nodes A, B and C!
 * const graph = new StateGraph(StateAnnotation)
 *   .addNode("nodeA", nodeA, {
 *     ends: ["nodeB", "nodeC"],
 *   })
 *   .addNode("nodeB", nodeB)
 *   .addNode("nodeC", nodeC)
 *   .addEdge("__start__", "nodeA")
 *   .compile();
 * 
 * await graph.invoke({ foo: "" });
 *
 * // Randomly oscillates between
 * // { foo: 'a|c' } and { foo: 'a|b' }
 * ```
 */
export class Command<
  Resume = unknown,
  Update extends Record<string, unknown> = Record<string, unknown>,
  Nodes extends string = string,
> extends CommandInstance<Resume, Update, Nodes> {
  readonly lg_name = "Command";

  lc_direct_tool_output = true;

  /**
   * Graph to send the command to. Supported values are:
   *   - None: the current graph (default)
   *   - The specific name of the graph to send the command to
   *   - {@link Command.PARENT}: closest parent graph (only supported when returned from a node in a subgraph)
   */
  graph?: string;

  /**
   * Update to apply to the graph's state as a result of executing the node that is returning the command.
   * Written to the state as if the node had simply returned this value instead of the Command object.
   */
  update?: Update | [string, unknown][];

  /**
   * Value to resume execution with. To be used together with {@link interrupt}.
   */
  resume?: Resume;

  /**
   * Can be one of the following:
   *   - name of the node to navigate to next (any node that belongs to the specified `graph`)
   *   - sequence of node names to navigate to next
   *   - {@link Send} object (to execute a node with the exact input provided in the {@link Send} object)
   *   - sequence of {@link Send} objects
   */
  goto?: Nodes | Send<Nodes> | (Nodes | Send<Nodes>)[] = [];

  static PARENT = "__parent__";

  constructor(args: Omit<CommandParams<Resume, Update, Nodes>, "lg_name">) {
    super(args);
    this.resume = args.resume;
    this.graph = args.graph;
    this.update = args.update;
    if (args.goto) {
      type ValidArg = Nodes | Send<Nodes, Update>;

      this.goto = Array.isArray(args.goto)
        ? (_deserializeCommandSendObjectGraph(args.goto) as ValidArg[])
        : [_deserializeCommandSendObjectGraph(args.goto) as ValidArg];
    }
  }

  /**
   * Convert the update field to a list of {@link PendingWrite} tuples
   * @returns List of {@link PendingWrite} tuples of the form `[channelKey, value]`.
   * @internal
   */
  _updateAsTuples(): PendingWrite[] {
    if (
      this.update &&
      typeof this.update === "object" &&
      !Array.isArray(this.update)
    ) {
      return Object.entries(this.update);
    } else if (
      Array.isArray(this.update) &&
      this.update.every(
        (t): t is [string, unknown] =>
          Array.isArray(t) && t.length === 2 && typeof t[0] === "string"
      )
    ) {
      return this.update;
    } else {
      return [["__root__", this.update]];
    }
  }

  toJSON() {
    let serializedGoto;
    if (typeof this.goto === "string") {
      serializedGoto = this.goto;
    } else if (_isSend(this.goto)) {
      serializedGoto = this.goto.toJSON();
    } else {
      serializedGoto = this.goto?.map((innerGoto) => {
        if (typeof innerGoto === "string") {
          return innerGoto;
        } else {
          return innerGoto.toJSON();
        }
      });
    }
    return {
      lg_name: this.lg_name,
      update: this.update,
      resume: this.resume,
      goto: serializedGoto,
    };
  }
}

/**
 * A type guard to check if the given value is a {@link Command}.
 *
 * Useful for type narrowing when working with the {@link Command} object.
 *
 * @param x - The value to check.
 * @returns `true` if the value is a {@link Command}, `false` otherwise.
 */
export function isCommand(x: unknown): x is Command {
  if (typeof x !== "object") {
    return false;
  }

  if (x === null || x === undefined) {
    return false;
  }

  if ("lg_name" in x && x.lg_name === "Command") {
    return true;
  }

  return false;
}

function isPlainObject(value: object): value is Record<string, unknown> {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Reconstructs Command and Send objects from a deeply nested tree of anonymous objects
 * matching their interfaces.
 *
 * This is only exported for testing purposes. It is NOT intended to be used outside of
 * the Command and Send classes.
 *
 * @internal
 *
 * @param x - The command send tree to convert.
 * @param seen - A map of seen objects to avoid infinite loops.
 * @returns The converted command send tree.
 */
export function _deserializeCommandSendObjectGraph(
  x: unknown,
  seen: Map<object, unknown> = new Map()
): unknown {
  if (x !== undefined && x !== null && typeof x === "object") {
    // If we've already processed this object, return the transformed version
    if (seen.has(x)) {
      return seen.get(x);
    }

    let result: unknown;

    if (Array.isArray(x)) {
      // Create the array first, then populate it
      result = [];
      // Add to seen map before processing elements to handle self-references
      seen.set(x, result);

      // Now populate the array
      x.forEach((item, index) => {
        (result as unknown[])[index] = _deserializeCommandSendObjectGraph(
          item,
          seen
        );
      });
      // eslint-disable-next-line no-instanceof/no-instanceof
    } else if (x instanceof Command || x instanceof Send || !isPlainObject(x)) {
      result = x;
      seen.set(x, result);
    } else if (isCommand(x)) {
      result = new Command(x);
      seen.set(x, result);
    } else if (_isSendInterface(x)) {
      result = new Send(
        x.node,
        x.args,
        x.timeout !== undefined ? { timeout: x.timeout } : undefined
      );
      seen.set(x, result);
    } else if ("lc_serializable" in x && x.lc_serializable) {
      result = x;
      seen.set(x, result);
    } else {
      // Create empty object first
      result = {};
      // Add to seen map before processing properties to handle self-references
      seen.set(x, result);

      // Now populate the object
      for (const [key, value] of Object.entries(x)) {
        (result as Record<string, unknown>)[key] =
          _deserializeCommandSendObjectGraph(value, seen);
      }
    }

    return result;
  }
  return x;
}
